import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { TelegramCopyRoute, TelegramGroup } from "@/lib/types";

const routesKey = ["telegram-copy-routes"] as const;

async function fetchCopyRoutes(): Promise<TelegramCopyRoute[]> {
  const { data, error } = await supabase
    .from("telegram_copy_routes")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? []) as TelegramCopyRoute[];
}

interface CreateCopyRouteInput {
  sourceGroup: TelegramGroup;
  destinationGroupId: string;
}

async function createCopyRoute(input: CreateCopyRouteInput): Promise<void> {
  const { error } = await supabase
    .from("telegram_copy_routes")
    .insert({
      tenant_id: input.sourceGroup.tenant_id,
      source_group_id: input.sourceGroup.id,
      destination_group_id: input.destinationGroupId,
      status: "active",
      transform_mode: "affiliate_links",
    });

  if (error) throw error;
}

async function updateCopyRouteStatus(input: {
  id: string;
  status: TelegramCopyRoute["status"];
}): Promise<void> {
  const { error } = await supabase
    .from("telegram_copy_routes")
    .update({ status: input.status })
    .eq("id", input.id);

  if (error) throw error;
}

async function deleteCopyRoute(id: string): Promise<void> {
  const { error } = await supabase
    .from("telegram_copy_routes")
    .delete()
    .eq("id", id);

  if (error) throw error;
}

export function useTelegramCopyRoutes() {
  return useQuery<TelegramCopyRoute[], Error>({
    queryKey: routesKey,
    queryFn: fetchCopyRoutes,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useCreateTelegramCopyRoute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCopyRoute,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: routesKey });
    },
  });
}

export function useUpdateTelegramCopyRouteStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCopyRouteStatus,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: routesKey });
    },
  });
}

export function useDeleteTelegramCopyRoute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCopyRoute,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: routesKey });
    },
  });
}
